import { useRef, useState } from 'react';

import type { MaterialOption } from './MaterialAutocomplete.types';

interface UseMaterialSearchStateArgs {
    /** The raw materials provided to the component, used to detect fresh results */
    materials: Array<MaterialOption>;
    /** The controlled input value, when the autocomplete is used in controlled mode */
    controlledInputValue?: string;
}

interface MaterialSearchState {
    /** The internally tracked input value */
    searchInputValue: string;
    /** Updates the internally tracked input value */
    setSearchInputValue: (value: string) => void;
    /** Whether the user is actively searching */
    isSearchActive: boolean;
    /**
     * Whether a freshly typed query is still waiting for matching results. While
     * pending, the loaded options are stale relative to the query, so the caller can
     * bridge the gap with the loading state instead of flashing a stale list.
     */
    isSearchPending: boolean;
}

/**
 * @description Tracks the autocomplete search input and derives whether the user is
 * actively searching and whether the currently loaded options are stale relative to
 * the typed query (so the caller can show a loading state until fresh results arrive).
 * @returns The search input value, its setter, and the derived search flags
 */
const useMaterialSearchState = ({
    materials,
    controlledInputValue,
}: UseMaterialSearchStateArgs): MaterialSearchState => {
    const [searchInputValue, setSearchInputValue] = useState('');

    const isSearchActive =
        searchInputValue.trim().length > 0 ||
        (controlledInputValue ?? '').trim().length > 0;

    /**
     * The query the user is currently searching for, combining the controlled
     * `inputValue` (when provided) with the internally tracked input value.
     */
    const activeSearchQuery = (controlledInputValue ?? searchInputValue).trim();

    /**
     * The loaded options are produced by the backend for a specific query. We track
     * which query they correspond to: `materials` gets a fresh reference whenever new
     * results arrive, which is our signal that they now match the active query.
     */
    const loadedOptionsRef = useRef(materials);
    const settledSearchQueryRef = useRef(activeSearchQuery);
    if (loadedOptionsRef.current !== materials) {
        loadedOptionsRef.current = materials;
        settledSearchQueryRef.current = activeSearchQuery;
    }

    const isSearchPending = activeSearchQuery !== settledSearchQueryRef.current;

    return {
        searchInputValue,
        setSearchInputValue,
        isSearchActive,
        isSearchPending,
    };
};

export { useMaterialSearchState };
export type { MaterialSearchState };
